import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface FunnelEvent {
  event_name: string;
  event_data?: Record<string, string | number | boolean>;
  page_url: string;
}

// Funnel stages in order
export const FunnelStages = {
  PAGE_LOAD: "FunnelPageLoad",
  SCROLL_25: "FunnelScroll25",
  SCROLL_50: "FunnelScroll50",
  SCROLL_75: "FunnelScroll75",
  SCROLL_100: "FunnelScroll100",
  SECTION_HERO: "FunnelSectionHero",
  SECTION_MODEL_SHOWCASE: "FunnelSectionModelShowcase",
  SECTION_SECOND_CTA: "FunnelSectionSecondCTA",
  SECTION_GALLERY: "FunnelSectionGallery",
  SECTION_WHY_LOVE: "FunnelSectionWhyLove",
  SECTION_SOCIAL_PROOF: "FunnelSectionSocialProof",
  SECTION_BUNDLE_CONTENTS: "FunnelSectionBundleContents",
  SECTION_BUSINESS: "FunnelSectionBusiness",
  SECTION_CATEGORIES: "FunnelSectionCategories",
  SECTION_COMPATIBILITY: "FunnelSectionCompatibility",
  SECTION_WHY_CHOOSE: "FunnelSectionWhyChoose",
  SECTION_TESTIMONIALS: "FunnelSectionTestimonials",
  SECTION_REVIEWS: "FunnelSectionReviews",
  SECTION_GUARANTEE: "FunnelSectionGuarantee",
  SECTION_FAQ: "FunnelSectionFAQ",
  SECTION_FINAL_CTA: "FunnelSectionFinalCTA",
  TIME_ON_PAGE_30S: "FunnelTimeOnPage30s",
  TIME_ON_PAGE_60S: "FunnelTimeOnPage60s",
  TIME_ON_PAGE_120S: "FunnelTimeOnPage120s",
  TIME_ON_PAGE_300S: "FunnelTimeOnPage300s",
  CTA_HOVER: "FunnelCTAHover",
  EXIT_INTENT: "FunnelExitIntent",
} as const;

export const useFunnelTracking = () => {
  const trackedEvents = useRef<Set<string>>(new Set());
  const sessionId = useRef<string>(crypto.randomUUID());
  const startTime = useRef<number>(Date.now());

  const logEvent = useCallback(async (eventName: string, eventData?: Record<string, string | number | boolean>) => {
    // Prevent duplicate events in same session
    const eventKey = `${eventName}-${JSON.stringify(eventData || {})}`;
    if (trackedEvents.current.has(eventKey)) return;
    trackedEvents.current.add(eventKey);

    const fullEventData = {
      ...eventData,
      session_id: sessionId.current,
      time_on_page_ms: Date.now() - startTime.current,
    };

    try {
      await supabase.from("meta_pixel_events").insert([{
        event_name: eventName,
        event_data: fullEventData,
        page_url: window.location.href,
      }]);
    } catch (error) {
      console.error("Failed to log funnel event:", error);
    }
  }, []);

  // Track page load
  useEffect(() => {
    logEvent(FunnelStages.PAGE_LOAD);
  }, [logEvent]);

  // Track scroll depth
  useEffect(() => {
    const scrollMilestones = { 25: false, 50: false, 75: false, 100: false };

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      if (scrollPercent >= 25 && !scrollMilestones[25]) {
        scrollMilestones[25] = true;
        logEvent(FunnelStages.SCROLL_25, { scroll_percent: 25 });
      }
      if (scrollPercent >= 50 && !scrollMilestones[50]) {
        scrollMilestones[50] = true;
        logEvent(FunnelStages.SCROLL_50, { scroll_percent: 50 });
      }
      if (scrollPercent >= 75 && !scrollMilestones[75]) {
        scrollMilestones[75] = true;
        logEvent(FunnelStages.SCROLL_75, { scroll_percent: 75 });
      }
      if (scrollPercent >= 95 && !scrollMilestones[100]) {
        scrollMilestones[100] = true;
        logEvent(FunnelStages.SCROLL_100, { scroll_percent: 100 });
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [logEvent]);

  // Track time on page
  useEffect(() => {
    const timers = [
      setTimeout(() => logEvent(FunnelStages.TIME_ON_PAGE_30S, { seconds: 30 }), 30000),
      setTimeout(() => logEvent(FunnelStages.TIME_ON_PAGE_60S, { seconds: 60 }), 60000),
      setTimeout(() => logEvent(FunnelStages.TIME_ON_PAGE_120S, { seconds: 120 }), 120000),
      setTimeout(() => logEvent(FunnelStages.TIME_ON_PAGE_300S, { seconds: 300 }), 300000),
    ];

    return () => timers.forEach(clearTimeout);
  }, [logEvent]);

  // Track exit intent (mouse leaving viewport at top)
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        logEvent(FunnelStages.EXIT_INTENT, { 
          time_on_page_seconds: Math.round((Date.now() - startTime.current) / 1000) 
        });
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [logEvent]);

  // Track section visibility using Intersection Observer
  const trackSection = useCallback((sectionName: string) => {
    logEvent(sectionName, { section: sectionName.replace("FunnelSection", "") });
  }, [logEvent]);

  // Track CTA hover
  const trackCTAHover = useCallback(() => {
    logEvent(FunnelStages.CTA_HOVER);
  }, [logEvent]);

  return { 
    logEvent, 
    trackSection,
    trackCTAHover,
    FunnelStages 
  };
};

// Hook for tracking section visibility
export const useSectionTracking = (sectionId: string, eventName: string) => {
  const ref = useRef<HTMLElement>(null);
  const tracked = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      async (entries) => {
        entries.forEach(async (entry) => {
          if (entry.isIntersecting && !tracked.current) {
            tracked.current = true;
            try {
              await supabase.from("meta_pixel_events").insert([{
                event_name: eventName,
                event_data: { section: sectionId },
                page_url: window.location.href,
              }]);
            } catch (error) {
              console.error("Failed to log section view:", error);
            }
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% visible
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [sectionId, eventName]);

  return ref;
};
